/**
 * The WhatsApp composer — the editor half of `/templates/:id` when the template
 * is a WhatsApp one.
 *
 * **It is not the email editor with different labels, and it must not become
 * one.** Three things differ, and each one shapes the screen:
 *
 *   1. **Variables are positional.** `{{1}}`, `{{2}}` — numbered, contiguous
 *      from 1, no names. The email parser would read `{{1}}` as a contact field
 *      called "1", so nothing here goes near it: every check on this screen is
 *      `collectWaTemplateVariableIssues` from `@wend/shared`, the same function
 *      the API refuses a save with. And a position is never typed by hand — it
 *      is inserted from the field list, which is what keeps the numbering
 *      contiguous and the token free of the spaces Meta rejects.
 *   2. **There are no attachments.** WhatsApp has a header instead, and a media
 *      header is a handle Meta gave back for bytes Meta already holds — not a
 *      file travelling with the message.
 *
 *      SLICE 9 CHANGED HOW THIS SCREEN GETS THAT HANDLE, AND THE OLD SENTENCE
 *      HERE READ *"the form asks for the handle and says why"*. It did, because
 *      nothing in Wend could produce one: there was no upload path anywhere, so
 *      a person was expected to arrive holding a handle minted elsewhere. Left
 *      standing, that sentence argues for a template pointing at bytes Wend
 *      never had — and a review handle cannot be re-minted from itself, so such
 *      a template can never be resubmitted after a rejection. The form now asks
 *      for the **file** (`WaHeaderMediaPanel`), Wend stores it, and the handle
 *      is derived from bytes we keep. What has not changed is the fact the old
 *      sentence was explaining: the header still carries a handle and not a
 *      file, which is why `waDocumentOf` still emits `mediaHandle`.
 *   3. **Meta approves the template, not the tenant.** A draft is edited here; a
 *      submitted one is read-only, because the document Meta reviewed and the
 *      document Wend holds have to be the same document. Editing reopens only
 *      through a rejection, which is `WaApprovalPanel`'s business.
 *
 * **Limits are Meta's, shown live.** Meta does not truncate — it refuses — so a
 * body at 1,030 characters is a refusal at submit time unless the count is on
 * screen while it is typed. Every ceiling is imported from `@wend/shared`.
 *
 * No cost, no spend, no currency: Meta meters conversations on the tenant's own
 * account and Wend meters credits. Neither is a price this screen could quote.
 *
 * **The draft model moved out in slice 9.** `WaDraft`, `waDraftOf`,
 * `waDocumentOf` and `applyStoredFile` now live in `./wa-draft.ts`, which
 * imports nothing but `@wend/shared`. It used to live here, and could not be
 * tested from here: this file imports the API client, which reads
 * `import.meta.env` and therefore only evaluates under Vite. That was a fair
 * trade while the conversion was three ternaries; it stopped being one when a
 * function that decides whether the submitted document points at bytes Wend
 * holds joined it.
 *
 * ## Slice 17 — what the cards stopped arguing
 *
 * Every card on this screen opened with a paragraph explaining a rule and then
 * showed a control that enforces it. The rules are in this header and in
 * `@wend/shared`; the descriptions now say what the field wants. Three of them
 * were kept close to full length because a reader would draw a false conclusion
 * without them, and it is worth naming which:
 *
 *   - **Meta reads the content, not the label.** The person who most needs this
 *     is the one choosing Utility to avoid a Marketing rate, and they are
 *     choosing it on this card;
 *   - **the footer is not an unsubscribe link.** Somebody arriving from the
 *     email editor, where the footer *is* the unsubscribe, would write "reply
 *     STOP" into it and opt nobody out;
 *   - **where a position gets filled.** A template whose `{{1}}` nothing fills,
 *     discovered after Meta has approved it, costs a review cycle.
 *
 *     SLICE 49 RETRACTED WHAT THIS SCREEN SAID ABOUT THAT. The callout read *"a
 *     template that uses variables can be submitted to Meta but not sent"*, and
 *     folded under it: *"Wend has nowhere to record that `{{1}}` means a
 *     contact's name … a campaign has no field for bindings."* Both were true
 *     when they were written and are now false in the worst direction a
 *     sentence can be — a screen telling every tenant that the feature they are
 *     about to use does not exist. A campaign records bindings, the
 *     Personalisation panel on the campaign is where they are chosen, and the
 *     schedule refuses with `wa_bindings_incomplete` until they are. So the
 *     warning is gone and what is left is a label naming the place: filled per
 *     campaign, not here. The argument is this paragraph, not a paragraph on
 *     the screen.
 *
 * **One sentence moved rather than shrank.** The buttons card said a quick reply
 * opens a 24-hour conversation on the tenant's Meta account. That is a real cost
 * and it is not dropped — `WA_BUTTON_TYPE_HELP.quick_reply` already carries it
 * on the Type field of every button, which is the control that causes it, so the
 * card is now the limit alone. Two copies of one consequence on one card is how
 * the second copy is the one that goes stale.
 */
import {
  WA_BUTTON_TYPE_VALUES,
  WA_HEADER_FORMAT_VALUES,
  WA_TEMPLATE_CATEGORY_VALUES,
  CONTACT_VARIABLE_NAMES,
  EXTRAS_VARIABLE_PREFIX,
  collectWaTemplateVariableIssues,
  summariseWaPositions,
  waTemplateDocumentSchema,
  waVariableToken,
  type Template,
  type WaButton,
  type WaHeaderFormat,
  type WaHeaderMediaAsset,
  type WaMediaFormat,
  type WaTemplateCategory,
} from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Select,
  cn,
  useToast,
} from '@wend/ui';
import { Plus, Save, Trash2, Variable } from 'lucide-react';
import {
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { FormAlert } from '../../../components/FormAlert';
import { Textarea } from '../../../components/Textarea';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../../lib/api/errors';
import { deleteTemplateAsset, updateTemplate } from '../../../lib/api/templates';
import { fetchWaHeaderMedia, uploadWaHeaderMedia } from '../../../lib/api/whatsapp';
import { validate, type FormErrors } from '../../../lib/form';
import { useAsyncData } from '../../../lib/use-async-data';
import {
  WA_BUTTON_TYPE_HELP,
  WA_BUTTON_TYPE_LABEL,
  WA_CATEGORY_DISPLAY,
  WA_HEADER_FORMAT_LABEL,
  WA_LIMITS,
  WA_MEDIA_HEADER_NOTE,
  remaining,
} from '../../../lib/whatsapp-display';
import {
  applyStoredFile,
  parsedWaDocument,
  waDocumentOf,
  type WaDraft,
} from './wa-draft';
import { WaHeaderMediaPanel } from './WaHeaderMediaPanel';

/* -------------------------------------------------------------------------- */
/* Variables                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The fields a position can stand for, derived from the contact contract.
 *
 * `CONTACT_VARIABLE_NAMES` is the contact schema's own keys, so this list cannot
 * go stale the way a hand-written one would. It is what the insert control
 * offers: a person picks *the field*, and Wend picks the number — which is the
 * only way the numbering stays contiguous from `{{1}}`, the rule Meta enforces
 * and SQL could not.
 */
const VARIABLE_SOURCES: readonly { value: string; label: string }[] = [
  ...CONTACT_VARIABLE_NAMES.map((name) => ({
    value: name,
    label:
      name === 'name'
        ? 'Contact’s name'
        : name === 'email'
          ? 'Contact’s email address'
          : 'Contact’s phone number',
  })),
  { value: `${EXTRAS_VARIABLE_PREFIX}Company`, label: 'An uploaded column (e.g. Company)' },
];

/**
 * Where a position gets its value — a label, and deliberately not a warning.
 *
 * One template is used by many campaigns, so what `{{1}}` stands for cannot
 * live here: it is a binding on the campaign, chosen on the Personalisation
 * panel of the campaign that uses this template. Nothing on this screen can
 * answer it and nothing on this screen refuses because of it — the schedule
 * does, with `wa_bindings_incomplete`, naming the position.
 *
 * A line of the same mono label as `in use ·` above it, rather than the warn
 * Callout that stood here until slice 49. That callout said a template with
 * variables could not be sent, which stopped being true the moment a campaign
 * could record a binding, and a screen that tells a tenant a working feature
 * does not exist is worse than a screen that says nothing at all.
 */
const VARIABLES_FILLED_PER_CAMPAIGN = 'filled per campaign · Personalisation panel';

/* -------------------------------------------------------------------------- */
/* The composer                                                                */
/* -------------------------------------------------------------------------- */

export interface WaComposerProps {
  template: Template;
  draft: WaDraft;
  /**
   * An updater, not a value — `Dispatch<SetStateAction<WaDraft>>`, which is
   * exactly what `useState` hands the editor page.
   *
   * It was `(next: WaDraft) => void` until slice 9, and the widening is not
   * cosmetic. Two of the handlers below are **async**: an upload and a removal
   * both close over the `draft` of the render that started them, and a person
   * typing in the body while a 4 MB PNG uploads would have had their sentence
   * replaced by the one that was on screen when they pressed the button. Every
   * synchronous handler still passes a plain object, which this type accepts
   * unchanged; the two async ones pass a function and read the current draft.
   */
  onDraftChange: Dispatch<SetStateAction<WaDraft>>;
  /** Meta owns a submitted document, so it is read here and edited nowhere. */
  readOnly: boolean;
  onSaved: (template: Template) => void;
}

export function WaComposer({
  template,
  draft,
  onDraftChange,
  readOnly,
  onSaved,
}: WaComposerProps) {
  const { toast } = useToast();
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const headerRef = useRef<HTMLInputElement | null>(null);
  /** Which field an inserted token lands in. The last one that had focus. */
  const [target, setTarget] = useState<'body' | 'header'>('body');

  const document = useMemo(() => parsedWaDocument(draft), [draft]);
  const issues = useMemo(
    () => (document === null ? null : collectWaTemplateVariableIssues(document)),
    [document],
  );

  /**
   * The file behind the media header, read once per template.
   *
   * Read here rather than taken off `template.assets`, because that shape is
   * slice 4's and knows nothing about a Meta handle — and the handle is what
   * separates "a file waiting for a submission" from "a file the header can
   * carry". It is fetched even while the header is text or absent, because a
   * stored file that the header no longer uses is exactly the orphan slice 4's
   * review caught, and a person cannot remove one they are never shown.
   */
  const media = useAsyncData((signal) => fetchWaHeaderMedia(template.id, signal), [template.id]);

  /**
   * What an upload or a removal did, overriding what the read said.
   *
   * `undefined` means "nothing has happened here, use the server's answer" —
   * which is a third state, and it is not `null`: `null` is the perfectly good
   * answer "this template has no file". Re-fetching after an upload would have
   * avoided the extra state and thrown away the asset the upload already
   * returned, for one round trip and a flash of the old value.
   */
  const [changed, setChanged] = useState<WaHeaderMediaAsset | null | undefined>(undefined);
  const asset = changed === undefined ? media.data : changed;

  function set<K extends keyof WaDraft>(key: K, value: WaDraft[K]): void {
    onDraftChange({ ...draft, [key]: value });
  }

  /** Moving the format re-derives the media fields from whatever is stored. */
  function setHeaderFormat(next: WaHeaderFormat | 'none'): void {
    onDraftChange(applyStoredFile({ ...draft, headerFormat: next }, next, asset));
  }

  /**
   * Stores the bytes, then rewires the document from what came back.
   *
   * The request lives here rather than in the panel so the panel imports no API
   * client: `lib/api/client.ts` reads `import.meta.env`, which only exists under
   * Vite, and a component that reaches it cannot be rendered by a verify script.
   * The wording on that panel is the thing most worth asserting, so it is the
   * thing kept renderable.
   */
  async function uploadHeaderMedia(file: File, format: WaMediaFormat): Promise<void> {
    const uploaded = await uploadWaHeaderMedia(template.id, file, format);
    setChanged(uploaded);
    // The updater form, not `draft`: this closure was made before the upload
    // started, and anything typed while it ran must survive it.
    onDraftChange((current) => applyStoredFile(current, current.headerFormat, uploaded));
  }

  /**
   * Removes the file, through slice 4's asset route.
   *
   * There is no WhatsApp-specific removal endpoint on purpose: a `wa_header_media`
   * row is a `template_assets` row, and `DELETE /templates/:id/assets/:assetId`
   * already drops the row and its stored object together, under the same tenant
   * guard. A second route would be a second chance for those two to fall out of
   * step.
   */
  async function removeHeaderMedia(assetId: string): Promise<void> {
    await deleteTemplateAsset(template.id, assetId);
    setChanged(null);
    // The bytes are gone, so the handle minted from them points at something we
    // can never mint again. Keeping it would leave a template that reads as
    // submittable and cannot be fixed after a rejection — the precise state this
    // slice exists to remove.
    onDraftChange((current) => ({ ...current, headerHandle: '', headerFileName: '' }));
  }

  /**
   * Inserts the next free position at the cursor.
   *
   * The number is Wend's, not the author's: `summariseWaPositions` says what is
   * already used, and the next one is the first gap or one past the end. That is
   * what makes "no gaps from {{1}}" true by construction rather than by a
   * warning after the fact.
   */
  function insertVariable(): void {
    if (readOnly) return;
    const used = issues?.positions.positions ?? [];
    const summary = summariseWaPositions(used);
    const next = summary.missing[0] ?? (used.length === 0 ? 1 : Math.max(...used) + 1);
    const token = waVariableToken(next);

    if (target === 'header') {
      const element = headerRef.current;
      const at = element?.selectionStart ?? draft.headerText.length;
      set('headerText', splice(draft.headerText, at, token));
      return;
    }
    const element = bodyRef.current;
    const at = element?.selectionStart ?? draft.body.length;
    set('body', splice(draft.body, at, token));
  }

  async function onSave(): Promise<void> {
    if (pending || readOnly) return;
    setRequestId(undefined);

    const result = validate(waTemplateDocumentSchema, waDocumentOf(draft));
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);
    try {
      const saved = await updateTemplate(template.id, {
        name: draft.name,
        whatsapp: result.data,
      });
      onSaved(saved);
      toast({
        tone: 'success',
        title: 'Saved',
        description: 'Nothing has been sent to Meta — submitting is a separate action.',
      });
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
    }
    setPending(false);
  }

  const bodyCount = remaining(draft.body, WA_LIMITS.body);
  const footerCount = remaining(draft.footer, WA_LIMITS.footer);
  const headerCount = remaining(draft.headerText, WA_LIMITS.header);

  return (
    <div className="flex flex-col gap-5">
      {readOnly && (
        <Callout tone="info" title="Meta has this template — it cannot be edited">
          A rejection reopens editing. Until then the stored document has to stay the one Meta
          reviewed.
        </Callout>
      )}

      {errors[ROOT_FIELD] !== undefined && (
        <FormAlert tone="error" {...(requestId === undefined ? {} : { requestId })}>
          {errors[ROOT_FIELD]}
        </FormAlert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-[1rem]">Category and language</CardTitle>
          {/*
            Kind 2. Meta reading the content rather than the label is the single
            most common reason a template comes back rejected, and a person
            choosing Utility to avoid a Marketing rate is exactly who needs it.
          */}
          <CardDescription>
            Meta reads the content, not the label. A promotional message filed as Utility is
            recategorised or rejected.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <FormField
            label="Category"
            required
            description={WA_CATEGORY_DISPLAY[draft.category].consequence}
            error={errors.category}
          >
            <Select
              value={draft.category}
              disabled={readOnly}
              options={WA_TEMPLATE_CATEGORY_VALUES.map((value) => ({
                value,
                label: `${WA_CATEGORY_DISPLAY[value].label} — ${WA_CATEGORY_DISPLAY[value].purpose}`,
              }))}
              onValueChange={(value) => {
                set('category', value as WaTemplateCategory);
              }}
            />
          </FormField>

          <FormField
            label="Language"
            required
            description="Meta’s code — en, en_US, hi. A template is approved per language."
            error={errors.language}
          >
            <Input
              value={draft.language}
              disabled={readOnly}
              spellCheck={false}
              placeholder="en"
              className="max-w-[10rem] font-mono text-[0.9rem]"
              onChange={(event) => {
                set('language', event.target.value);
              }}
            />
          </FormField>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[1rem]">Header</CardTitle>
          <CardDescription>
            Optional, and one line. {WA_MEDIA_HEADER_NOTE}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <FormField label="Header type" error={errors.header}>
            <Select
              value={draft.headerFormat}
              disabled={readOnly}
              options={[
                { value: 'none', label: 'No header' },
                ...WA_HEADER_FORMAT_VALUES.map((value) => ({
                  value,
                  label: WA_HEADER_FORMAT_LABEL[value],
                })),
              ]}
              onValueChange={(value) => {
                setHeaderFormat(value as WaHeaderFormat | 'none');
              }}
            />
          </FormField>

          {draft.headerFormat === 'text' && (
            <FormField
              label="Header text"
              required
              description={`At most ${String(WA_LIMITS.header)} characters, and at most one variable — Meta allows no more in a header.`}
              error={errors['header.text']}
            >
              <div className="flex flex-col gap-1.5">
                <Input
                  ref={headerRef}
                  value={draft.headerText}
                  disabled={readOnly}
                  onFocus={() => {
                    setTarget('header');
                  }}
                  onChange={(event) => {
                    set('headerText', event.target.value);
                  }}
                />
                <Counter used={headerCount.used} limit={WA_LIMITS.header} over={headerCount.over} />
              </div>
            </FormField>
          )}

          {/*
            The handle and the file name were two text inputs until slice 9, and
            both are gone rather than merely disabled. The handle is derived from
            the stored file — see `applyStoredFile` — because a typed one points
            at bytes Wend does not have. The file name went with it for a second
            reason: it described itself as "shown to the recipient for a document
            header", and `waMediaHeaderSchema.fileName` says the opposite in as
            many words — *"shown in the composer only. Meta never sees it."* One
            of the two was a confident claim about what a provider displays, and
            it was the one on this screen.
          */}
          {draft.headerFormat !== 'none' && draft.headerFormat !== 'text' && (
            <div className="flex flex-col gap-2">
              <WaHeaderMediaPanel
                format={draft.headerFormat}
                asset={asset}
                loading={media.loadingFirst}
                loadError={media.status === 'error' ? media.error : null}
                onRetry={media.reload}
                readOnly={readOnly}
                documentHandle={draft.headerHandle}
                onUpload={uploadHeaderMedia}
                onRemove={removeHeaderMedia}
              />

              {/*
                The save's own verdict, printed where the field used to be. The
                document still carries a handle, so `header.mediaHandle` is still
                the path the API and the contract report against — and a file
                that stored but never minted one lands here rather than nowhere.
              */}
              {errors['header.mediaHandle'] !== undefined && (
                <p className="text-[0.85rem] text-bad">{errors['header.mediaHandle']}</p>
              )}
            </div>
          )}

          {/*
            A stored file the header no longer uses. Nothing references it — a
            WA header asset has the same property as an attachment, which is why
            slice 4's review made the orphan sweep filter on `kind` — so no save
            will ever remove it and this is the only place it can be seen.
          */}
          {(draft.headerFormat === 'none' || draft.headerFormat === 'text') && asset !== null && (
            <Callout tone="warn" title="A header file is still stored for this template">
              {asset.filename} is kept against this template and this header does not use it. No
              save will remove it. Set the header back to{' '}
              {WA_HEADER_FORMAT_LABEL[asset.format].toLowerCase()} to use it, or to remove it.
            </Callout>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[1rem]">Body</CardTitle>
          <CardDescription>
            Insert variables from the list below rather than typing braces — Meta requires the
            numbering to run contiguously from {'{{1}}'}, and Wend keeps it that way.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <FormField label="Message" required error={errors.body}>
            <div className="flex flex-col gap-1.5">
              <Textarea
                ref={bodyRef}
                rows={7}
                value={draft.body}
                disabled={readOnly}
                onFocus={() => {
                  setTarget('body');
                }}
                onChange={(event) => {
                  set('body', event.target.value);
                }}
              />
              <Counter used={bodyCount.used} limit={WA_LIMITS.body} over={bodyCount.over} />
            </div>
          </FormField>

          <VariableInsert
            disabled={readOnly}
            positions={issues?.positions.positions ?? []}
            target={target}
            onInsert={insertVariable}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[1rem]">Footer</CardTitle>
          {/*
            Kind 2, both halves. Somebody arriving from the email editor expects
            the footer to carry an unsubscribe link and would put one here; on
            WhatsApp opting out is a reply and a setting on the Meta account, so
            a hand-written "reply STOP" line in a footer opts nobody out.
          */}
          <CardDescription>
            Optional, small grey text. No variables — Meta refuses those — and it is not an
            unsubscribe link: on WhatsApp, opting out is a reply and a setting on your Meta
            account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField label="Footer" error={errors.footer}>
            <div className="flex flex-col gap-1.5">
              <Input
                value={draft.footer}
                disabled={readOnly}
                onChange={(event) => {
                  set('footer', event.target.value);
                }}
              />
              <Counter used={footerCount.used} limit={WA_LIMITS.footer} over={footerCount.over} />
            </div>
          </FormField>
        </CardContent>
      </Card>

      <ButtonsCard
        buttons={draft.buttons}
        readOnly={readOnly}
        errors={errors}
        onChange={(buttons) => {
          set('buttons', buttons);
        }}
      />

      <IssuesPanel issues={issues} usesVariables={(issues?.positions.positions.length ?? 0) > 0} />

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            size="sm"
            loading={pending}
            onClick={() => {
              void onSave();
            }}
          >
            <Save aria-hidden="true" className="size-4" />
            {pending ? 'Saving' : 'Save'}
          </Button>
          <p className="text-[0.85rem] text-muted">
            Saving changes nothing at Meta. Submitting for approval is a separate action.
          </p>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pieces                                                                      */
/* -------------------------------------------------------------------------- */

function splice(value: string, at: number, insert: string): string {
  return `${value.slice(0, at)}${insert}${value.slice(at)}`;
}

/** `312 / 1024`, red once it is over. Meta refuses rather than truncating. */
function Counter({ used, limit, over }: { used: number; limit: number; over: boolean }) {
  return (
    <p
      className={cn(
        'text-right font-mono text-[0.72rem] tabular-nums',
        over ? 'text-bad' : 'text-muted',
      )}
    >
      {used} / {limit}
      {over && <span className="ml-2 uppercase tracking-[0.1em]">Meta will refuse this</span>}
    </p>
  );
}

/**
 * Insert a variable by choosing the field it stands for.
 *
 * The field list is what a person picks from; the *number* is Wend's, taken from
 * what the document already uses. Typing `{{ 1 }}` or jumping from `{{1}}` to
 * `{{3}}` are both refusals from Meta, and both are impossible from here.
 */
function VariableInsert({
  disabled,
  positions,
  target,
  onInsert,
}: {
  disabled: boolean;
  positions: readonly number[];
  target: 'body' | 'header';
  onInsert: () => void;
}) {
  const [source, setSource] = useState(VARIABLE_SOURCES[0]?.value ?? 'name');

  return (
    <div className="flex flex-col gap-3 rounded-base border border-line bg-white/[0.02] p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <FormField
            label="Insert a variable"
            description={`It goes into the ${target === 'header' ? 'header' : 'body'} at the cursor, numbered for you.`}
          >
            <Select
              value={source}
              disabled={disabled}
              options={VARIABLE_SOURCES.map((entry) => ({
                value: entry.value,
                label: entry.label,
              }))}
              onValueChange={setSource}
            />
          </FormField>
        </div>
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={onInsert}>
          <Variable aria-hidden="true" className="size-4" />
          Insert
        </Button>
      </div>

      {positions.length > 0 && (
        <p className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
          in use · {positions.map((position) => waVariableToken(position)).join(' ')}
        </p>
      )}

      {/*
        Drawn beside the list of positions and only once one exists: it answers
        "and what fills these?", which is a question nobody has before they have
        inserted one.

        NO `uppercase` ON THIS ONE LINE. It is byte-for-byte the same mono class
        list as the `in use ·` readout above, and rendered that way the two read
        as one block — a navigation hint set exactly like a data readout. Worse,
        `uppercase` flattened "Personalisation panel" into PERSONALISATION
        PANEL, so the proper name of the card this points at stopped looking
        like a name. The mono stays, because it is still a label rather than a
        sentence. It is deliberately not a link: from the template editor there
        is no campaign to link to, and one template is used by many.
      */}
      {positions.length > 0 && (
        <p className="font-mono text-[0.72rem] tracking-[0.1em] text-muted">
          {VARIABLES_FILLED_PER_CAMPAIGN}
        </p>
      )}
    </div>
  );
}

function ButtonsCard({
  buttons,
  readOnly,
  errors,
  onChange,
}: {
  buttons: readonly WaButton[];
  readOnly: boolean;
  errors: FormErrors;
  onChange: (buttons: WaButton[]) => void;
}) {
  function update(index: number, next: WaButton): void {
    onChange(buttons.map((button, at) => (at === index ? next : button)));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1rem]">Buttons</CardTitle>
        {/*
          The 24-hour-conversation consequence is not dropped, it is moved to
          where it is acted on: `WA_BUTTON_TYPE_HELP.quick_reply` carries it on
          the Type field of every button, which is the control that causes it.
          Saying it twice on one card is how the second copy goes stale.
        */}
        <CardDescription>At most {WA_LIMITS.buttons}.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {buttons.length === 0 && (
          <p className="text-[0.9rem] text-muted">No buttons. The message is text alone.</p>
        )}

        {buttons.map((button, index) => (
          <div
            key={index}
            className="flex flex-col gap-4 rounded-base border border-line bg-white/[0.02] p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <Badge tone="neutral">Button {index + 1}</Badge>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove button ${String(index + 1)}`}
                  onClick={() => {
                    onChange(buttons.filter((_, at) => at !== index));
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                  Remove
                </Button>
              )}
            </div>

            <FormField label="Type" description={WA_BUTTON_TYPE_HELP[button.type]}>
              <Select
                value={button.type}
                disabled={readOnly}
                options={WA_BUTTON_TYPE_VALUES.map((value) => ({
                  value,
                  label: WA_BUTTON_TYPE_LABEL[value],
                }))}
                onValueChange={(value) => {
                  update(
                    index,
                    value === 'url'
                      ? { type: 'url', text: button.text, url: '' }
                      : { type: 'quick_reply', text: button.text },
                  );
                }}
              />
            </FormField>

            <FormField
              label="Label"
              required
              description={`At most ${String(WA_LIMITS.buttonLabel)} characters.`}
              error={errors[`buttons.${String(index)}.text`]}
            >
              <div className="flex flex-col gap-1.5">
                <Input
                  value={button.text}
                  disabled={readOnly}
                  onChange={(event) => {
                    update(index, { ...button, text: event.target.value });
                  }}
                />
                <Counter
                  used={remaining(button.text, WA_LIMITS.buttonLabel).used}
                  limit={WA_LIMITS.buttonLabel}
                  over={remaining(button.text, WA_LIMITS.buttonLabel).over}
                />
              </div>
            </FormField>

            {button.type === 'url' && (
              <FormField
                label="Link"
                required
                description="https:// only. A variable may sit at the very end of the URL and nowhere else — Meta refuses one in the middle."
                error={errors[`buttons.${String(index)}.url`]}
              >
                <Input
                  value={button.url}
                  disabled={readOnly}
                  spellCheck={false}
                  placeholder="https://example.in/offer"
                  className="font-mono text-[0.85rem]"
                  onChange={(event) => {
                    update(index, { ...button, url: event.target.value });
                  }}
                />
              </FormField>
            )}
          </div>
        ))}

        {!readOnly && buttons.length < WA_LIMITS.buttons && (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                onChange([...buttons, { type: 'quick_reply', text: '' }]);
              }}
            >
              <Plus aria-hidden="true" className="size-4" />
              Add a button
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What still stops this template being submitted.
 *
 * Every message is `@wend/shared`'s own — the same sentences the API refuses a
 * save with — printed unchanged. A second account of the same problem, written
 * here, would eventually disagree with the one that actually blocks the save.
 */
function IssuesPanel({
  issues,
  usesVariables,
}: {
  issues: ReturnType<typeof collectWaTemplateVariableIssues> | null;
  usesVariables: boolean;
}) {
  if (issues === null) {
    return (
      <Callout tone="info" title="Not a complete template yet">
        A WhatsApp template needs at least a category and a body. Checks against Meta’s rules run as
        soon as there is something to check.
      </Callout>
    );
  }

  const messages = [
    ...issues.tokenIssues.map((issue) => issue.message),
    ...issues.documentIssues.map((issue) => issue.message),
  ];

  if (messages.length === 0) {
    return (
      <Callout tone="good" title="Nothing here would be refused by Meta’s formatting rules">
        Meta still reviews the content itself, which is a judgement Wend cannot make for it.
        {usesVariables && ' This template uses variables, so it can be submitted but not sent.'}
      </Callout>
    );
  }

  return (
    <Callout tone="bad" title="Fix these before submitting">
      <ul className="ml-4 list-disc space-y-1">
        {messages.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    </Callout>
  );
}
